import ContractingFooter from "features/contracting/components/ContractingFooter";
import ContractingStepFormProps from "features/contracting/models/ContractingStepFormProps";
import { useContractingStep } from "features/contracting/hooks/useContractingStep";
import React from "react";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import { useForm } from "features/shared/forms/hooks/useForm";
import PaymentMethodTypeSubForm from "features/contracting/components/PaymentMethodTypeSubForm";
import { paymentMethodFormValidations } from "./paymentMethodFormValidation";
import {
  getFormInitValues,
  isBankAccountPaymentMethod,
  isSipoPaymentMethod,
} from "./paymentMethodFormHelper";
import PageCard from "features/shared/components/card/PageCard";
import BankAccountSubForm from "features/payments/components/bankAccount/BankAccountSubForm";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import SipoAccountSubForm from "features/payments/components/sipoAccount/SipoAccountSubForm";
import { validateBankAccountNumber } from "features/payments/components/bankAccount/bankAccountValidations";
import { validateSipo } from "features/payments/components/sipoAccount/sipoAccountValidations";
import _ from "lodash";
import BankTypeEnumDTO from "features/shared/enums/dynamic/BankTypeEnumDTO";
import { Currency } from "features/shared/enums/currency";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";
import DepositsAmountSubForm from "features/contracting/components/paymentMethod/DepositsAmountSubForm";
import { useContractingCustomerTypeValue } from "features/contracting/hooks/useContractingCustomerTypeValue";
import { CustomerType } from "features/customer/enums/customerType";

const PaymentMethodFormSection = (props: ContractingStepFormProps) => {
  //translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "paymentMethodFormSection");

  // store
  const stepData = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).progress.steps[props.actualStep.component];

  const configuration = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).configuration;

  const customerType =
      useContractingCustomerTypeValue<CustomerType>("customerType");

  const banks: BankTypeEnumDTO[] = configuration.banks!;
  const paymentTypes: Record<string, string[]> = configuration.paymentTypes!;
  
  // default currency
  const paymentTypesLang: Currency = "CZK"; // TODO: add to configuraction

  // form hook
  const formHandler = useForm({
    initialValues: getFormInitValues(stepData.values, paymentTypesLang),
    initialValidations: paymentMethodFormValidations(),
  });

  const {
    values,
    errors,
    bindFieldNew,
    bindReactSelectField,
    valideteForm,
    setValidationRule,
    setValidationError,
    validateFormField,
    clearFormValues,
    isFilledFormValues,
  } = { ...formHandler };

  // helpers
  const validateEntireForm = (): boolean => {
    let isValid = false;

    if (valideteForm()) {
      isValid = true;

      if (isBankAccountPaymentMethod(values)) {
        isValid =
          isValid &&
          validateBankAccountNumber(
            values,
            validateFormField,
            setValidationError
          );
      }
      if (isSipoPaymentMethod(values)) {
        isValid =
          isValid &&
          validateSipo(
            _.get(values, "sipoNumber"),
            validateFormField,
            setValidationError
          );
      }
    }

    return isValid;
  };

  const getPaymentTypes = (): string[] => { 
    const typesByLang = paymentTypes[paymentTypesLang];

    if (customerType === "Company") {
      // for company disable SIPO
      return typesByLang.filter(x => x !== "Sipo");
    } else {
      return typesByLang;
    }
  }

  // form-step hook
  const { onPrevClick, onSaveClick, onNextClick } = useContractingStep({
    ...props,
    formValues: values,
    validateForm: validateEntireForm,
  });

  // render
  return (
    <div>
      <PaymentMethodTypeSubForm
        paymentTypes={getPaymentTypes()}
        formHandler={formHandler}
      />
      <RenderWrapper isVisible={() => isSipoPaymentMethod(values)}>
        <PageCard title={tLabels("sipoCardTitle")} bodyClassName="pt-4 px-4" cardClassName="mb-4">
          <div className="row no-gutters mx-n2">
            <div className="col-md-6 px-2">
              <SipoAccountSubForm
                bindField={bindFieldNew}
                clearFormValues={clearFormValues}
                setValidationRule={setValidationRule}
                setValidationError={setValidationError}
                validateFormField={validateFormField}
                errors={errors}
              />
            </div>
          </div>
        </PageCard>
        <PaymentMethodTypeSubForm
          paymentTypes={getPaymentTypes()}
          formHandler={formHandler}
          onlyInvoicePaymentTypes
        />
      </RenderWrapper>
      <RenderWrapper isVisible={() => isBankAccountPaymentMethod(values)}>
        <PageCard
          title={tLabels("bankAccountCardTitle")}
          bodyClassName="pt-4 px-4"
          cardClassName="mb-4"
        >
          <BankAccountSubForm
            bindField={bindFieldNew}
            bindReactSelectField={bindReactSelectField}
            clearFormValues={clearFormValues}
            setValidationRule={setValidationRule}
            setValidationError={setValidationError}
            validateFormField={validateFormField}
            errors={errors}
            banks={banks}
          />
        </PageCard>
      </RenderWrapper>
      <DepositsAmountSubForm 
        bindField={bindFieldNew}
        errors={errors}
        currency={paymentTypesLang}
      />

      <ContractingFooter
        onPrevClick={onPrevClick}
        onNextClick={onNextClick}
        onSaveClick={props.enableSaveBtn ? onSaveClick : undefined}
      />
    </div>
  );
};

export default PaymentMethodFormSection;
